import { Rx } from '@/lib/regulation-terms';
import { motion } from 'motion/react';
import { Link } from '@tanstack/react-router';
import {
  Bug, Clock, History, Activity, Plane, Pill, Network, Telescope, Dna,
  Gamepad2, Flame, KeyRound, ShieldCheck, Radio, Waves, Box, Target,
  Brain, Lock, ShieldAlert, Database, GitBranch, Cpu, Sparkles, Atom,
} from 'lucide-react';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';

interface Innovation {
  num: number;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  source: string;       // Where the idea is borrowed from (aerospace / banking / etc.)
  what: string;         // What it does, one short sentence
  why: string;          // Why it works / what other DSPMs don't have
  module: 'Discovery' | 'Activity' | 'Detection' | 'Frontier' | 'Cross';
  to?: string;           // Where in the app it's implemented
  competitor?: string;   // Whose moat this beats
}

const INNOVATIONS: Innovation[] = [
  {
    num: 1, name: 'Honey-PII Canary Tokens', icon: Bug, source: 'InfoSec honeypots', module: 'Discovery', to: '/dspm/honey-pii',
    what: 'Decoy PII values seeded across data; any read fires an instant alert.',
    why: 'No false positives. A real attacker doesn\'t know which Aadhaar values are real vs canaries.',
    competitor: 'Purview, BigID, Cyera have nothing equivalent.',
  },
  {
    num: 2, name: 'Time-Travel Discovery', icon: Clock, source: 'Software Versioning', module: 'Discovery',
    what: 'Show every PII column that EVER existed at this asset, not just current state.',
    why: 'Answers "did this column ever hold Aadhaar?" - critical for breach audits + subject data discovery.',
    competitor: 'DSPMs show only "current state" - miss columns deleted/renamed/migrated.',
  },
  {
    num: 3, name: 'Compliance Replay Engine', icon: History, source: 'CI/CD playback', module: 'Frontier', to: '/frontier/replay',
    what: '"What if RBI 6h had applied 90 days earlier?" - counter-factual replay across 13 regulators.',
    why: 'Quantify regulatory exposure BEFORE a new framework hits production.',
    competitor: 'No DSPM/DAM/DDR offers counter-factual simulation.',
  },
  {
    num: 4, name: 'SPC on Classification Rates', icon: Activity, source: 'Manufacturing Six Sigma', module: 'Discovery',
    what: 'Statistical Process Control flags when classifier accuracy drifts beyond 3σ.',
    why: 'Catches model degradation BEFORE it causes false negatives at scale.',
    competitor: 'Cyera/Sentra have no automated drift detection on classifier itself.',
  },
  {
    num: 5, name: 'Aviation Black Box Provenance', icon: Plane, source: 'Aviation FDR', module: 'Cross',
    what: 'Per-record provenance chain from source → every transform → every reader.',
    why: 'When a record breaches, prove who touched it, when, why, with what consent.',
    competitor: 'Purview has lineage; ours is per-record + tamper-evident.',
  },
  {
    num: 6, name: 'Pharma Chain of Custody', icon: Pill, source: 'Pharmaceutical CoC', module: 'Cross',
    what: 'Every PII handoff signed + timestamped + countersigned by next custodian.',
    why: 'Auditor-grade evidence for "who exactly had this data, end-to-end."',
    competitor: 'No DSPM offers signed handoffs.',
  },
  {
    num: 7, name: 'Epidemiology Contact Tracing', icon: Network, source: 'CDC Contact Tracing', module: 'Cross',
    what: 'Once breach is confirmed, trace every actor/system that touched the same data within 30 days.',
    why: 'Compute actual blast radius - not just direct accessors but downstream copies.',
    competitor: 'IBM Guardium has after-the-fact forensics; ours surfaces blast radius proactively.',
  },
  {
    num: 8, name: 'Cosmology Dark Matter Inference', icon: Telescope, source: 'Astronomy', module: 'Discovery',
    what: 'Even when scanner can\'t directly read a column, infer PII presence from query patterns.',
    why: 'Critical for No-Direct-DB-Access mode - find PII without ever connecting to the DB.',
    competitor: 'Industry-first capability - no other DSPM has it.',
  },
  {
    num: 9, name: 'Genome-Style Obfuscated PII', icon: Dna, source: 'Bioinformatics', module: 'Discovery',
    what: 'Find PII even when actors deliberately obfuscated it (base64, hex, Caesar shift, etc.).',
    why: 'Defends against insider threats trying to hide their data exfiltration.',
    competitor: 'Purview/BigID only match exact patterns; we beat naive obfuscation.',
  },
  {
    num: 10, name: 'Game Dev Level-of-Detail Scanning', icon: Gamepad2, source: 'Game engine LOD', module: 'Discovery',
    what: 'Adaptive sampling rate - scan small tables fully, sample huge tables intelligently.',
    why: '100M-row tables scanned in minutes instead of hours, with the same statistical confidence.',
    competitor: 'Cyera scans linearly; we scan adaptively.',
  },
  {
    num: 11, name: 'Forest Fire Containment Protocol', icon: Flame, source: 'Wildfire firebreaks', module: 'Detection', to: '/detection/playbooks',
    what: 'When one asset is compromised, automatically quarantine N-hop neighbors via column-graph traversal.',
    why: 'Prevent lateral spread BEFORE the attacker pivots.',
    competitor: 'No DDR product has automated graph-based containment.',
  },
  {
    num: 12, name: 'Banking Two-Person Rule', icon: KeyRound, source: 'Bank vault access', module: 'Detection', to: '/detection/break-glass',
    what: 'Break-glass + critical playbooks require a second distinct approver.',
    why: 'No single insider can bypass controls; auditor + regulator-grade.',
    competitor: 'Standard in banking, almost never in DSPM/DAM/DDR.',
  },
  {
    num: 13, name: 'Aerospace Triple Redundancy', icon: ShieldCheck, source: 'Aerospace flight control', module: 'Detection', to: '/detection/decisions',
    what: '3 engines vote (rule_engine + UEBA + policy_graph). 3-of-3 = block; 2-of-3 = step-up; 1-of-3 = alert (degraded).',
    why: 'Hard-blocking a senior employee on a single false positive is a business catastrophe. Triple redundancy prevents that.',
    competitor: 'No DDR product uses aerospace-style redundant decisioning.',
  },
  {
    num: 14, name: 'Earthquake P-Wave Indicators', icon: Radio, source: 'Earthquake Early Warning', module: 'Activity', to: '/activity/p-wave',
    what: '6 detectors fire seconds BEFORE a breach pattern completes (schema mapping, audit-log disable, credential creation, off-pattern, cold-table burst, mass-export).',
    why: 'Block in advance, not after; convert detection from reactive to predictive.',
    competitor: 'Imperva/Guardium are reactive; we\'re predictive.',
  },
  {
    num: 15, name: 'Submarine Sonar Profiling', icon: Waves, source: 'Submarine acoustic sigs', module: 'Activity', to: '/activity/sonar',
    what: 'Per-application acoustic signatures (statementType + table + hour distributions); KL-divergence flags app-behavior drift.',
    why: 'Detects "this app started behaving like a different app" - classic credential abuse signal.',
    competitor: 'UEBA in Splunk/Securonix is per-user; ours is per-application.',
  },
  {
    num: 16, name: 'Submarine Compartmentalization', icon: Box, source: 'Submarine watertight compartments', module: 'Cross',
    what: 'Every module/tenant/data-class isolated; one breach can\'t flood the whole stack.',
    why: 'Customer-VPC isolation + RLS + per-tenant audit chain + isolated KMS keys.',
    competitor: 'Most DSPMs share infra across tenants; we\'re sovereign-isolated.',
  },
  {
    num: 17, name: 'Six Sigma Quality Gates', icon: Target, source: 'Manufacturing Six Sigma', module: 'Discovery',
    what: 'Classifier model promoted to production only when accuracy ≥ 99.7% on holdout.',
    why: 'No model regression slips through; regulatory-grade reliability.',
    competitor: 'Industry first to apply Six Sigma DMAIC to DSPM model lifecycle.',
  },
  {
    num: 18, name: 'Federated Classifier Improvement', icon: Brain, source: 'Federated learning', module: 'Frontier', to: '/frontier/federated',
    what: 'Cross-tenant classifier improvement via Flower SecAgg+. Coordinator never sees plaintext deltas.',
    why: 'Get cross-customer model improvements without sharing customer data.',
    competitor: 'No DSPM today - true sovereign-grade ML.',
  },
  {
    num: 19, name: 'SMPC Cross-Tenant Benchmarks', icon: Atom, source: 'Secure Multi-Party Computation', module: 'Frontier', to: '/frontier/smpc',
    what: 'Aggregate metrics ("how many tenants have ≥80% Aadhaar Vault coverage?") via Mersenne-61 additive secret sharing.',
    why: 'Customers see industry benchmarks WITHOUT exposing per-tenant inputs.',
    competitor: 'Cybersecurity benchmarking is normally vendor-aggregated; we\'re cryptographic.',
  },
  {
    num: 20, name: 'Quantum-Safe Tokenisation', icon: Lock, source: 'NIST PQC Round 3', module: 'Frontier', to: '/frontier/pq',
    what: 'CRYSTALS-Kyber for KEM, CRYSTALS-Dilithium for signatures. Audit chain quantum-resilient.',
    why: 'When quantum cracks RSA, our audit chain stays tamper-evident.',
    competitor: 'No production DSPM/DAM/DDR ships PQC today.',
  },
  {
    num: 21, name: 'DPDPA Section 9 Engine', icon: ShieldAlert, source: 'India DPDPA 2023', module: 'Discovery', to: '/dspm/section-9',
    what: 'Specialised detection for minor PII without verifiable parental consent. ₹200 Cr exposure per violation.',
    why: 'No global DSPM has Section 9-specific detection. India regulatory moat.',
    competitor: 'Purview/BigID/Cyera only have generic minor-data flags.',
  },
  {
    num: 22, name: 'Aadhaar Vault Compliance Detector', icon: ShieldAlert, source: 'UIDAI 2025 Circular', module: 'Discovery',
    what: 'Detects Aadhaar in non-vault columns; auto-recommends migration to UIDAI Vault.',
    why: 'UIDAI penalties + regulator audits - must-have for Indian BFSI.',
    competitor: 'Industry first.',
  },
  {
    num: 23, name: "Find a person's data (graph traversal)", icon: GitBranch, source: 'Column-graph (sibling-walk on data_asset_columns)', module: 'Discovery', to: '/dspm/reverse-dsar',
    what: 'Hashed identifier in → all related findings out via BFS through asset graph.',
    why: 'DPDPA Right to Erasure + GDPR Article 17 compliance in seconds, not weeks.',
    competitor: 'Manual + slow in OneTrust/Securiti; ours is automated.',
  },
  {
    num: 24, name: 'Synthetic-Error Contract', icon: Database, source: 'Mainframe SQL conventions', module: 'Detection', to: '/detection/decisions',
    what: 'One BLK-XXXXXXXXXXXX Request ID flows through SQL error → audit chain → SIEM ticket → Why-Blocked screen.',
    why: 'No translation tables anywhere. Single source of truth for incident correlation.',
    competitor: 'Industry-first - saves 80% of incident-correlation time.',
  },
  {
    num: 25, name: '5-Engine Classifier Voting', icon: Cpu, source: 'Multi-instrument astronomy', module: 'Discovery',
    what: '5 classifier engines vote per finding (regex+checksum / DistilBERT-Indic NER / column-graph sibling-walk / pgvector embeddings / Tier 5 BYO-LLM).',
    why: 'When one engine fails, others cover. Classifier never goes blind.',
    competitor: 'Purview is single-engine; BigID is single-engine; we have 5-engine fault tolerance.',
  },
];

const ICON_BG: Record<Innovation['module'], string> = {
  Discovery: 'from-chart-1/30 to-chart-2/20',
  Activity: 'from-chart-2/30 to-chart-7/20',
  Detection: 'from-chart-7/30 to-chart-1/20',
  Frontier: 'from-chart-2/30 to-chart-7/20',
  Cross: 'from-muted/40 to-muted/20',
};

export function Innovations() {
  return (
    <PageContainer>
      <PageHeader
        title="Innovation Catalogue"
        subtitle="A reference catalogue of the cross-domain techniques applied across the platform. Each entry names the source discipline, the FOCTTA application, and the in-product surface where the technique is implemented."
        badges={[{ label: '25 entries', variant: 'glass' }, { label: 'Reference', variant: 'info' }]}
      />

      <GlassCard className="p-4 mb-6 border-l-4 border-l-info">
        <div className="text-xs text-muted-foreground leading-relaxed">
          Each technique below is applied in production code paths within Discovery, Activity, Detection, or Frontier. Click "See it live" on any entry to view the running implementation. Comparative analysis against industry approaches and detailed architecture diagrams are maintained in the engineering brief at <code>docs/technical-brief/EXPERT-REVIEW-BRIEF.html</code>.
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {INNOVATIONS.map((inv, i) => {
          const Icon = inv.icon;
          const Card = (
            <GlassCard shine className="p-5 h-full lift">
              <div className="flex items-start justify-between mb-3">
                <div className={`h-10 w-10 rounded-md bg-gradient-to-br ${ICON_BG[inv.module]} flex items-center justify-center text-primary`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[9px] font-mono text-muted-foreground">#{String(inv.num).padStart(2, '0')}</span>
                  <Badge variant="glass" className="text-[9px]">{inv.module}</Badge>
                </div>
              </div>

              <h3 className="text-sm font-display font-semibold leading-tight mb-1"><Rx>{inv.name}</Rx></h3>
              <div className="text-[10px] text-muted-foreground mb-2">borrowed from <em className="text-foreground/80">{inv.source}</em></div>

              <p className="text-xs text-foreground/90 leading-snug mb-2"><Rx>{inv.what}</Rx></p>

              <div className="text-xs text-muted-foreground leading-snug border-l-2 border-info pl-2 mb-2">
                <strong className="text-foreground not-italic">Application:</strong> <Rx>{inv.why}</Rx>
              </div>

              {inv.to && (
                <Link to={inv.to} className="text-[11px] text-primary hover:underline mt-3 inline-flex items-center gap-1">
                  See it live →
                </Link>
              )}
            </GlassCard>
          );
          return (
            <motion.div
              key={inv.num}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.025, duration: 0.35 }}
            >
              {Card}
            </motion.div>
          );
        })}
      </div>

    </PageContainer>
  );
}
